import { computed, ref } from "vue";
import Camera from "simple-vue-camera";
import { useDeviceStore } from "@/stores/device.store";
import { defineStore } from "pinia";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { usePermission } from "@vueuse/core";
import { datadogLogs } from "@datadog/browser-logs";

const CAMERA_RESOLUTION = { width: 500, height: 500 };
const CAMERA_QUALITY = 0.5;

export const useCameraStore = defineStore("camera", () => {
    const deviceStore = useDeviceStore();
    const isCameraActive = ref(false);
    const isCameraLoading = ref(false);
    const cameraRef = ref<InstanceType<typeof Camera>>();

    const { t } = useI18n({ useScope: "global" });
    const cameraPermission = usePermission("camera");

    const takePicture = async () => {
        const cameraReference = cameraRef.value;
        if (!cameraReference) {
            throw new Error(t("camera.notFound"));
        }

        if (!isStreamHealthy()) {
            console.warn("Camera stream unhealthy before snapshot, restarting...");
            await stopCamera();
            await cameraReference.start();
            await new Promise((resolve) => setTimeout(resolve, 500));
            if (!isStreamHealthy()) {
                throw new Error(t("camera.streamNotRestored"));
            }
        }

        const cameraPictureBlob = await cameraReference.snapshot(CAMERA_RESOLUTION, "image/png", CAMERA_QUALITY);
        if (!cameraPictureBlob) {
            return undefined;
        }

        const pictureAsBase64 = await blobToBase64(cameraPictureBlob);
        return pictureAsBase64;
    };

    const blobToBase64 = (pictureBlob: Blob) => {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(pictureBlob);
        });
    };

    const isStreamHealthy = (): boolean => {
        // No underlying MediaStream — camera was never started or already fully stopped
        const stream = cameraRef.value?.stream as MediaStream | undefined;
        if (!stream) return false;

        // Stream exists but has no video track — e.g. device was physically disconnected
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) return false;

        // "live" means the track is actively producing frames (vs "ended" after stop/disconnect).
        // "muted" is true when the track exists but delivers no data — this happens when the OS
        // suspends the camera (e.g. another app takes exclusive access, or the tab is backgrounded
        // on some mobile browsers). A muted track still has readyState "live", so we need both checks
        // to reliably detect a frozen stream that would produce black/stale snapshots.
        return videoTrack.readyState === "live" && !videoTrack.muted;
    };

    const stopCamera = async () => {
        try {
            cameraRef.value?.stop();
            isCameraActive.value = false;
        } catch (error) {
            console.error("Error stopping camera", error);
        }
    };

    const startCamera = async () => {
        console.debug("Starting camera...");
        if (!deviceStore.isAuthenticated) return;

        if (cameraPermission.value === "denied") {
            console.debug("Camera permission denied, skipping start");
            return;
        }

        if (isCameraActive.value && !isStreamHealthy()) {
            console.debug("Camera stream is unhealthy, restarting...");
            await stopCamera();
        }

        if (isCameraActive.value && isStreamHealthy()) return;

        try {
            await cameraRef.value?.start();
        } catch (error) {
            datadogLogs.logger.error("camera start failed", { error });
            location.reload();
        }
    };

    const isCameraFeatureActive = computed(() => {
        return deviceStore.deviceConfig?.takePictureWhenClockingInOrOut ?? false;
    });

    const handleCameraError = async (error: Error) => {
        datadogLogs.logger.error("camera permission error", { error });

        toast(t("camera.permissionError"), { type: "error" });
    };

    const setCameraRef = (cam: InstanceType<typeof Camera> | undefined) => {
        cameraRef.value = cam;
    };

    const loading = () => {
        isCameraLoading.value = true;
    };

    const setIsActive = (isActive: boolean) => {
        isCameraLoading.value = false;
        isCameraActive.value = isActive;
    };

    return {
        loading,
        setIsActive,
        setCameraRef,
        isCameraActive,
        isCameraLoading,
        handleCameraError,
        takePicture,
        isCameraFeatureActive,
        startCamera,
        stopCamera,
        isStreamHealthy,
    };
});
